import { useEffect } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Sidebar, SidebarHeader, close, toggle, useSidebarClosing } from '@/features/sidebar';
import { useVideoLibrary, useVideoData, VideoPreview } from '@/features/video-library';
import { servicesApi } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { AccountState } from '@/services/auth/types';
import { useSupportChat } from '@/features/support';
import { isEmptyString } from '@/utils/string';
import {
  Box,
  Button,
  Divider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Heading,
  Link,
  Scrollable,
  SkeletonShape,
  Stack,
} from '@customerio/pluma-components/react';
import { ResourceLink } from './components/resource-link';
import { SystemStatus } from './components/system-status';
import { useCustomerFeedback } from '@/features/customer-feedback';
import { useQueryParamAction } from '@/hooks/use-query-param-action';
import * as styles from './index.css';

const SIDEBAR_ID = 'help-dropdown';

const SECTION_COUNT = 4;

const sectionVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      delay: i * 0.06,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  }),
  exit: (i: number) => ({
    opacity: 0,
    y: -6,
    transition: {
      duration: 0.15,
      delay: (SECTION_COUNT - 1 - i) * 0.02,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  }),
};

/** Instant variants when user prefers reduced motion (no animation). */
const sectionVariantsReducedMotion = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0 } },
  exit: { opacity: 0, transition: { duration: 0 } },
};

export function useWorkrampAuth() {
  const auth = useAuth();

  const handleWorkrampAuth = async (dest: string, fallbackUrl: string) => {
    const accountId = auth.account?.id;
    const userId = auth.user?.id;

    if (accountId == null || userId == null) {
      window.open(fallbackUrl, '_blank');
      return;
    }

    try {
      const response = await servicesApi.getWorkrampAuthUrl({
        params: { accountId, userId },
        query: { dest },
      });

      if (!isEmptyString(response.url)) {
        window.open(response.url, '_blank');
      } else {
        window.open(fallbackUrl, '_blank');
      }
    } catch (_error) {
      // Fall back to static URL if the API call fails
      // Note: This might be blocked by popup blockers since it's async
      window.open(fallbackUrl, '_blank');
    }
  };

  return { handleWorkrampAuth };
}

export function HelpSidebar() {
  const handleClose = () => {
    void close();
  };

  const auth = useAuth();
  const { track } = useAnalytics();
  const aiEnabled = useFeatureFlag(AccountFeatureFlag.AiEnabled);
  const isAiAssistantV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const { isHandoverAllowed, openChat, openForm } = useSupportChat();
  const { handleWorkrampAuth } = useWorkrampAuth();
  const showFeedbackModal = useCustomerFeedback();
  const showCommunity = auth.account?.account_state === AccountState.Current;
  const { openVideoLibrary } = useVideoLibrary();
  const { data: videos = [], isLoading: isLoadingVideos, isError: isErrorVideos } = useVideoData();
  const featuredVideos = videos.slice(0, 3);
  const showEducationSection = !isErrorVideos;

  // Expose openForm globally so the Ember ui app can open the support form directly
  useEffect(() => {
    if (window.__hydra_sidebar__ != null) {
      window.__hydra_sidebar__.openSupportForm = (source?: string, initialProblem?: string) => {
        openForm(initialProblem, source);
      };
    }

    return () => {
      if (window.__hydra_sidebar__ != null) {
        window.__hydra_sidebar__.openSupportForm = () => {
          // No-op: HelpSidebar has unmounted.
        };
      }
    };
  }, [openForm]);

  // Handle query parameters for opening modals
  useQueryParamAction('support', () => {
    openForm();
  });

  useQueryParamAction('feedback', () => {
    void showFeedbackModal();
  });

  useQueryParamAction('videos', () => {
    openVideoLibrary();
  });

  const handleVideoClick = (videoId: string) => {
    void track('customer_io_basics', 'video_clicked', { id: videoId });
    openVideoLibrary(videoId);
  };

  const isClosing = useSidebarClosing(SIDEBAR_ID);
  const shouldReduceMotion = useReducedMotion();
  const variants = shouldReduceMotion === true ? sectionVariantsReducedMotion : sectionVariants;

  return (
    <Sidebar
      id={SIDEBAR_ID}
      onToggle={(isOpen) => {
        if (isOpen) {
          void track('help_center', 'opened', {});
        }
      }}
    >
      <div className={styles.container}>
        <motion.div variants={variants} initial="hidden" animate={isClosing ? 'exit' : 'visible'} custom={0}>
          <SidebarHeader title="Help & Support" onClose={handleClose} />
        </motion.div>
        <div className={styles.content}>
          <motion.div variants={variants} initial="hidden" animate={isClosing ? 'exit' : 'visible'} custom={1}>
            <Stack gap="200">
              <Heading level="6">Resources</Heading>
              <Stack>
                <ResourceLink
                  title="Customer.io Documentation"
                  icon="plaintext"
                  href="https://docs.customer.io/"
                  isExternal
                  onClick={() => {
                    void track('help_center', 'platformDocsClicked', {});
                  }}
                />
                <ResourceLink
                  title="API Documentation"
                  icon="transactional-api"
                  href="https://docs.customer.io/integrations/api/customerio-apis/"
                  isExternal
                  onClick={() => {
                    void track('help_center', 'apiDocsClicked', {});
                  }}
                />
                {showCommunity ? (
                  <ResourceLink
                    title="Ask the Community"
                    icon="social"
                    onClick={async () => {
                      void track('help_center', 'askCommunityClicked', {});
                      await handleWorkrampAuth('community', 'https://academy.customer.io/community');
                    }}
                  />
                ) : null}
                <ResourceLink
                  title="Release Notes"
                  icon="feature-launch"
                  href="https://docs.customer.io/release-notes/"
                  isExternal
                  onClick={() => {
                    void track('help_center', 'releaseNotesClicked', {});
                  }}
                />
                <ResourceLink
                  title="Connect with a Partner"
                  icon="wait"
                  href="https://customer.io/agency-partners?utm_medium=in%20app&utm_source=partners&utm_campaign=find_a_partner_cta_fly&utm_term=pmm"
                  isExternal
                  onClick={() => {
                    void track('help_center', 'connectWithPartnerClicked', {});
                  }}
                />
              </Stack>
            </Stack>
            <Divider my="200" />
          </motion.div>

          <motion.div variants={variants} initial="hidden" animate={isClosing ? 'exit' : 'visible'} custom={2}>
            <Stack gap="100">
              {showEducationSection ? (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Heading level="6">Education</Heading>
                    <Link onClick={() => openVideoLibrary()}>View all</Link>
                  </Box>
                  <Scrollable overflow="x">
                    <Box display="flex" gap="200">
                      {isLoadingVideos
                        ? Array.from({ length: 3 }).map((_, i) => (
                            <Box key={i} flexShrink="0" style={{ width: '220px' }} p="100">
                              <SkeletonShape style={{ width: '100%', aspectRatio: '16 / 9', marginBottom: '8px' }} />
                              <SkeletonShape style={{ width: '80%', height: '16px', marginBottom: '4px' }} />
                              <SkeletonShape style={{ width: '60%', height: '12px' }} />
                            </Box>
                          ))
                        : featuredVideos.map((video) => (
                            <Box key={video.id} flexShrink="0" style={{ width: '220px' }}>
                              <VideoPreview
                                video={video}
                                direction="column"
                                onSelect={() => handleVideoClick(video.id)}
                              />
                            </Box>
                          ))}
                    </Box>
                  </Scrollable>
                </>
              ) : null}
              <Stack gap="050" pt="200">
                <ResourceLink
                  title="Join the Customer.io Academy"
                  icon="winner"
                  description="Get started and become a Customer.io expert."
                  onClick={async () => {
                    void track('help_center', 'learnAcademyClicked', {});
                    await handleWorkrampAuth('academy', 'https://academy.customer.io/academy');
                  }}
                />
                <ResourceLink
                  title="Explore a sample workspace"
                  icon="workspaces"
                  description="Pretend data and view only"
                  href="/sample-workspace"
                  onClick={() => {
                    void close();
                  }}
                />
                <Divider my="200" />
                <ResourceLink
                  title="Send us feedback"
                  onClick={() => {
                    void track('help_center', 'feedbackRequestStarted', {});
                    void showFeedbackModal();
                  }}
                />
                <SystemStatus />
              </Stack>
            </Stack>
          </motion.div>

          {/* In-app messaging target for Customer.io messages */}
          <div id="in-app-target_help-center" />
        </div>
        <motion.div
          className={styles.footer}
          variants={variants}
          initial="hidden"
          animate={isClosing ? 'exit' : 'visible'}
          custom={3}
        >
          <Box display="flex" gap="100" justifyContent="flex-end">
            <Button
              variant="secondary"
              href="/settings/privacy?expandAccessControls=true"
              onClick={() => {
                void track('help_center', 'grantSupportAccessClicked', {});
              }}
            >
              Grant support access
            </Button>
            <DropdownMenu placement="top-end">
              <DropdownMenuTriggerButton variant="primary" icon="chat">
                Get help
              </DropdownMenuTriggerButton>
              <DropdownMenuContent>
                {aiEnabled ? (
                  <DropdownMenuItem
                    icon="chat"
                    description="Fastest way to get help"
                    onClick={() => {
                      void track('help_center', 'aiAssistantClicked', {});
                      toggle('copilot');
                    }}
                  >
                    {isAiAssistantV2 === true ? 'Chat with AI Agent' : 'Chat with AI Assistant'}
                  </DropdownMenuItem>
                ) : null}
                {isHandoverAllowed ? (
                  <DropdownMenuItem
                    icon="chat-question"
                    description="Monday - Friday 5:00 am - 8:00 pm EST"
                    onClick={() => {
                      void track('help_center', 'chatWithSupportClicked', {});
                      void openChat();
                    }}
                  >
                    Chat with support
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  icon="help"
                  description="Monday - Friday 5:00 am - 8:00 pm EST"
                  onClick={() => {
                    void track('help_center', 'helpRequestStarted', {});
                    openForm();
                  }}
                >
                  Submit a support request
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Box>
        </motion.div>
      </div>
    </Sidebar>
  );
}
